(ns common.widgets.editable-label
  (:require ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["moment/min/moment-with-locales$default" :as moment]
            ["react" :as react]
            ["react-use/lib/useEvent$default" :as useEvent]
            [cljs-bean.core :refer [->js]]
            [common.util :refer [format-iso-string]]
            [common.widgets.datepicker :as datepicker]
            [common.widgets.dropdown :as dropdown]
            [common.widgets.input :as input]
            [uix.core :as uix :refer [$ defui]]))

(defonce no-value-assigned "No value assigned")

(defmulti ^:private dropdown-selected-label
  "Return the selected label for the dropdown label.
   `:single` => selected `:label` from options as string
   `:multi`  => selected labels from options as unordered list (hiccup)"
  (comp #(if (coll? %)
           :multi
           :single)
        :value))

(defmethod dropdown-selected-label :single
  [{:keys [options value]}]
  (if (nil? value)
    no-value-assigned
    (->> options
         (filter #(= value (:value %)))
         first
         :label)))

(defmethod dropdown-selected-label :multi
  [{values :value
    options :options}]
  (let [labels (->> options
                    (filter #((set values) (:value %)))
                    (map :label))]
    (if (empty? labels)
      no-value-assigned
      labels)))

(defmulti select-component
  (fn [component _label _set-on-edit] component))

(defmethod select-component :chip
  [_ label set-edit-mode]
  [:> Chip {:label    label
            :on-click #(set-edit-mode true)
            :on-focus #(set-edit-mode true)}])

(defmethod select-component :menu-select
  [_ label set-edit-mode]
  [:> Select {:value label
              :size "small"}
   [:> MenuItem {:value label
                 :on-click #(set-edit-mode true)
                 :on-focus #(set-edit-mode true)}
    label]])

(defn dropdown
  "Returns hiccup for an editable label by the use of a dropdown
   component by clicking on it."
  [{:keys [on-change value options multi? selected-component stack-direction]
    :or {multi? false selected-component :chip stack-direction "row"}}]
  (let [[edit-mode set-edit-mode] (react/useState false)

        on-keydown (react/useCallback (fn [e]
                                        (when edit-mode
                                          (case (.-key e)
                                            "Escape" (set-edit-mode false)
                                            nil))))]

    (useEvent "keydown" on-keydown)

    (if edit-mode
      [dropdown/dropdown
       {:auto-focus? true
        :value value
        :value-renderer #(-> (js->clj % :keywordize-keys true) :label str)
        :options options
        :open-on-focus? true
        :on-blur #(set-edit-mode false)
        :on-change #(do (on-change %)
                        (set-edit-mode false))
        :multi? multi?
        :clearable? true}]
      (let [labels (dropdown-selected-label {:options options :value value})]
        [:> Stack {:direction stack-direction
                   :spacing 0.5}
         (for [label (if (coll? labels) labels [labels])]
           (-> (select-component selected-component label set-edit-mode)
               (with-meta {:key label})))]))))

(defui clear-button [{:keys [on-clear]}]
  ($ IconButton
     {:onMouseDown #(do (.stopPropagation %)
                        (on-clear nil))
      :sx #js {:padding 0.5
               "input[type='number'] + &" {:ml 0.5}}}
     ($ Icon {:fontSize "small"} "clear")))

(def input-clear-button
  (uix/as-react
   (fn [{:keys [input-value clear-value children] :as props}]
     ($ InputAdornment (-> (dissoc props :input-value :clear-value) ->js)
        (when input-value
          ($ clear-button {:on-clear clear-value}))
        children))))

(defui datepicker
  "Returns hiccup of an editable label by the use of a datepicker
   component by clicking on it."
  [{:keys [on-change value]}]
  (let [[focus-value set-focus-value] (uix/use-state value)
        [input-value set-input-value] (uix/use-state value)
        [edit-mode set-edit-mode] (uix/use-state false)

        submit-change #(when (not= focus-value input-value)
                         (on-change input-value)
                         (set-edit-mode false))
        on-keydown (uix/use-callback (fn [e]
                                       (when edit-mode
                                         (case (.-key e)
                                           "Escape" (do (set-input-value focus-value)
                                                        (set-edit-mode false))
                                           "Enter" (submit-change)
                                           nil)))
                                     [edit-mode focus-value submit-change])
        on-focus-input #(set-focus-value (moment (.. % -target -value) "DD-MM-YYYY HH:mm"))
        on-change-input #(set-input-value %)

        clear-value #(do (set-input-value nil)
                         (set-edit-mode false))]

    (useEvent "keydown" on-keydown)

    (if edit-mode
      ($ datepicker/datetime-picker
         {:autoFocus true
          :value input-value
          :onChange on-change-input
          :onFocus on-focus-input
          :slots #js {:inputAdornment input-clear-button}
          :slotProps #js {:inputAdornment #js {:input-value input-value
                                               :clear-value clear-value}}
          :sx #js {:width "100%"}})
      ($ Chip
         {:label (if input-value
                   (format-iso-string input-value)
                   no-value-assigned)
          :onClick #(set-edit-mode true)
          :onFocus #(set-edit-mode true)}))))

(defui input
  "Returns hiccup for an editable label by the use of an input
   component by clicking on it."
  [{:keys [on-change value inputProps disabled?]}]
  (let [[focus-value set-focus-value] (uix/use-state "")
        [input-value set-input-value] (uix/use-state value)
        [edit-mode set-edit-mode] (uix/use-state false)

        submit-change #(when (not= focus-value input-value)
                         (on-change input-value)
                         (set-edit-mode false))
        on-keydown (uix/use-callback (fn [e]
                                       (when edit-mode
                                         (case (.-key e)
                                           "Escape" (do (set-input-value focus-value)
                                                        (set-edit-mode false))
                                           "Enter" (submit-change)
                                           nil)))
                                     [edit-mode focus-value submit-change])
        on-focus-input #(set-focus-value (.. % -target -value))
        on-change-input #(set-input-value (.. % -target -value))
        on-blur-input submit-change

        clear-value #(do (set-input-value nil)
                         (set-edit-mode false))]

    (useEvent "keydown" on-keydown)

    (uix/use-effect (fn []
                      (set-input-value value))
                    [value])

    (if edit-mode
      ($ input/input {:inputProps inputProps
                      :autoFocus true
                      :value input-value
                      :onFocus on-focus-input
                      :onBlur on-blur-input
                      :onChange on-change-input
                      :endAdornment ($ clear-button {:on-clear clear-value})})
      ($ Chip {:label (or input-value no-value-assigned)
               :onClick (when-not disabled? #(set-edit-mode true))
               :onFocus (when-not disabled? #(set-edit-mode true))}))))

(defui clickable-label
  [{:keys [on-click on-clear value]}]
  ($ Chip (cond-> {:onClick on-click
                   :label (or value no-value-assigned)}
            (and on-clear value) (assoc :onDelete on-clear))))
